import React, { FC, ReactElement, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import CheckboxField from 'buyside/components/presentation/Fields/checkboxField';
import InputField from 'buyside/components/presentation/Fields/inputField';
import SelectField from 'buyside/components/presentation/Fields/selectField';

import { labelize } from 'buyside/util/arrayHelper';
import { formatCompensations } from 'buyside/util/formatNumber';

const {
  assetClassNames,
  cities,
  compFundStrategy,
  compIndustry,
  compTitle,
  fundSizes,
  fundTypeNames,
  yearOfExp
} = require('buyside/util/constantsFromRails.json');
const cityNames = cities.map((city: { id: number; name: string }) => city.name);

import {
  CreateOrUpdateCompensationMutationVariables,
  InputMaybe
} from 'interfaces/graphql-typings';

import styles from './compensationSubmissionPageStyles.scss';

export const calculateTotal = (arr: (InputMaybe<number> | undefined)[]): number =>
  arr.reduce<number>((sum, num) => (num ? (sum += num) : sum), 0);

const CompensationForm: FC<{ disabled: boolean }> = ({ disabled }): ReactElement => {
  const { trigger, watch } = useFormContext<CreateOrUpdateCompensationMutationVariables>();

  const cashFields = watch([
    'baseSalary',
    'annualDiscrBonus',
    'interestIncentiveFeesValue',
    'interestMgmtFeesValue'
  ]);
  const totalCashComp = useMemo(() => calculateTotal(cashFields), [cashFields]);

  return (
    <>
      <h5>Role Details</h5>
      <SelectField
        disabled={disabled}
        isRequired
        label="Role"
        name="title"
        options={labelize(compTitle)}
      />
      <SelectField
        disabled={disabled}
        isRequired
        label="Years of Experience"
        name="experience"
        options={labelize(yearOfExp)}
      />
      <SelectField
        disabled={disabled}
        isRequired
        label="Industry"
        name="industry"
        options={labelize(compIndustry)}
      />
      <SelectField
        disabled={disabled}
        isRequired
        isSearchable
        label="Location"
        name="location"
        options={labelize(cityNames)}
      />
      <SelectField
        disabled={disabled}
        isRequired
        label="Firm Type"
        name="fundType"
        options={labelize(fundTypeNames)}
      />
      <SelectField
        disabled={disabled}
        isRequired
        label="Fund Strategy"
        name="fundStrategy"
        options={labelize(compFundStrategy)}
      />
      <SelectField
        disabled={disabled}
        isRequired
        label="Fund Size"
        name="fundSize"
        options={labelize(fundSizes)}
      />
      <SelectField
        disabled={disabled}
        isRequired
        label="Asset Class Expertise"
        name="assetClass"
        options={labelize(assetClassNames)}
      />
      <h5>
        Compensation <span>*Please annualize all numbers.</span>
      </h5>
      <InputField
        disabled={disabled}
        endAdornment="USD"
        inputProps={{ min: 0 }}
        isRequired
        label="Base Salary"
        name="baseSalary"
        onChangeCallback={() => {
          trigger('comments');
        }}
        type="number"
      />
      <div className={styles.feeType}>Management Fees</div>
      <section className={styles.subFieldGroup}>
        <InputField
          disabled={disabled}
          endAdornment="%"
          inputProps={{ min: 0 }}
          label="Percentage"
          name="interestMgmtFeesPerc"
          size="small"
          type="number"
        />
        <InputField
          disabled={disabled}
          endAdornment="USD"
          inputProps={{ min: 0 }}
          label="Cash"
          name="interestMgmtFeesValue"
          onChangeCallback={() => {
            trigger('comments');
          }}
          size="small"
          type="number"
        />
        <InputField
          disabled={disabled}
          endAdornment="years"
          inputProps={{ min: 0 }}
          label="Vesting Period"
          name="vestingMgmtFees"
          size="small"
          type="number"
        />
      </section>
      <div className={styles.feeType}>Incentive Fees</div>
      <section className={styles.subFieldGroup}>
        <InputField
          disabled={disabled}
          endAdornment="%"
          inputProps={{ min: 0 }}
          label="Percentage"
          name="interestIncentiveFeesPerc"
          size="small"
          type="number"
        />
        <InputField
          disabled={disabled}
          endAdornment="USD"
          inputProps={{ min: 0 }}
          label="Cash"
          name="interestIncentiveFeesValue"
          onChangeCallback={() => {
            trigger('comments');
          }}
          size="small"
          type="number"
        />
        <InputField
          disabled={disabled}
          endAdornment="years"
          inputProps={{ min: 0 }}
          label="Vesting Period"
          name="vestingIncentiveFees"
          size="small"
          type="number"
        />
      </section>
      <InputField
        disabled={disabled}
        endAdornment="USD"
        inputProps={{ min: 0 }}
        label="Other Cash Bonus"
        name="annualDiscrBonus"
        onChangeCallback={() => {
          trigger('comments');
        }}
        type="number"
      />
      <InputField
        disabled={disabled}
        endAdornment="USD"
        inputProps={{ min: 0 }}
        label="Deferred Compensation"
        name="totalDeferredComp"
        type="number"
      />
      <section className={styles.checkboxes}>
        <CheckboxField
          disabled={disabled}
          label="I am able to invest in the fund without paying fees."
          name="investInFund"
        />
        <CheckboxField
          disabled={disabled}
          label="This firm allows me to build a personal track record through managing a book or sub-portfolio."
          name="buildPersonalTrackRecord"
        />
        <CheckboxField
          disabled={disabled}
          label="This firm allows me to market my personal track record post-employment."
          name="marketPersonalTrackRecord"
        />
      </section>
      {cashFields[0] && totalCashComp < 100000 ? (
        <div className={styles.cashTotal}>
          <h6>Total Cash Compensation: {formatCompensations(totalCashComp)}</h6>
          The income you are entering is lower than expected. Please explain the circumstances of
          your situation below.
        </div>
      ) : null}
      <InputField
        disabled={disabled}
        label="Compensation Structure"
        name="comments"
        type="textarea"
        textAreaProps={{
          placeholder:
            "Include any additional information regarding the firm's compensation structure here."
        }}
      />
    </>
  );
};

export default CompensationForm;
